export const TERMS_VERSION = "2026-08-10";

export const PRIVACY_VERSION = "2026-08-10";

/* ---------------------------- backend helpers ---------------------------- */


export const API_BASE = "/api/household";

// Client-side mirror of the Worker's CODE_RE (worker/lib/constants.js). Used to tell a real
// household code apart from the placeholder "PREVIEW" that ?preview=app runs under, so
// network-touching features can opt out of preview rather than firing requests at a
// household that doesn't exist.
export const HOUSEHOLD_CODE_RE = /^[A-Z0-9]{6}$/;


export const DEVICE_CODE_KEY = "tq_device_code";

export const INVITE_OK_KEY = "tq_invite_ok";
// LB-184 (Patch C): the creation ticket /api/invite/validate returns alongside the pass. The
// pass still drives the gate UI; the ticket is what the worker now requires on the save that
// CREATES a household (handleStart / handleRestore), and it is removed once spent. 7-day
// lifetime server-side, so a parent who finishes set-up next weekend is not sent back to a
// gate that would refuse the code they already used.
export const CREATION_TICKET_KEY = "tq_creation_ticket";

export const HIDDEN_CHILDREN_KEY_PREFIX = "tq_hidden_children_";

// Per-device, per-child notification preference — mirrors HIDDEN_CHILDREN_KEY_PREFIX. Hiding
// a child on a device automatically adds them here too (see App.jsx's toggleChildVisibility)
// — a hidden child can't independently have notifications on for this device.
export const MUTED_CHILDREN_KEY_PREFIX = "tq_muted_children_";

// Which child this DEVICE was last looking at, per household. Purely a convenience: any
// reload used to snap back to profiles[0], so a parent refreshing while on their second
// child — or a child on their own shared-tablet profile — silently jumped to the first
// one. Per-device on purpose (like hidden/muted above): two devices in a household are
// usually two different people looking at two different children.
export const ACTIVE_CHILD_KEY_PREFIX = "tq_active_child_";

// Which Parent Mode tab this DEVICE was last on. Since 2 Oct (Alan) it is read only by a boot
// that lands in Parent Mode (a reload while there): entering Parent Mode from Kid Mode always
// opens Home. It never opens Parent Mode by itself, so it cannot weaken the PIN gate.
export const PARENT_TAB_KEY_PREFIX = "tq_parent_tab_";
// The waiting-requests overlay (2 Oct): ids of the pending requests this DEVICE has already
// shown in it, per household. A request listed once is not the reason to show it again on the
// next unlock; a new one brings the overlay back with everything that is waiting.
export const REQUESTS_OVERLAY_SHOWN_KEY_PREFIX = "lb_requests_overlay_shown_";

// Set the moment a device successfully joins a household (see Root.jsx's handleJoin).
// App.jsx reads this once, right at mount, to decide whether to open the device setup
// flow — then clears it immediately so it can never show a second time for this code.
// Keyed by household code (like HIDDEN_CHILDREN_KEY_PREFIX) so a device that later joins
// a *different* household gets its own fresh prompt.
//
// It used to be set only for households with more than one child, on the reasoning that
// "which kids show here" has no content when there is only one. Alan, 30 Aug 2026: ask
// anyway. With one child the question stops being about visibility and becomes about what
// this device IS — a child's, a shared one, or a grown-up's — which decides what it opens
// into and who gets alerts on it. Those matter whatever the household size.
export const PENDING_DEVICE_SETUP_KEY_PREFIX = "tq_pending_device_setup_";

// What this DEVICE is, per household: {"kind":"child","childId":"..."} | {"kind":"shared"}
// | {"kind":"parent"}. Written by the device setup flow (and by the same flow re-run from
// Settings), read at mount to decide the opening mode.
//
// Per-device like hidden/muted, and deliberately NOT part of household state: two devices
// in a household are two different people, and syncing this would have one person's answer
// overwrite another's. Absent means "never asked" — every device that joined before this
// existed, plus the household creator's own device, which skips the flow entirely.
export const DEVICE_ROLE_KEY_PREFIX = "tq_device_role_";

// Set on the device that CREATED a household (Root.jsx, both the fresh-create and the
// restore paths), keyed by code. The creator's device has no device role until the wizard
// is done — that prompt is deferred — so this is the only thing that says "this is the
// parent's phone" while setup is still in progress. App.jsx reads it to decide that a
// mid-setup reload boots into Parent Mode behind the PIN rather than Kid Mode; a device
// that merely JOINED an unfinished household has no marker and keeps its kid-first boot
// (LB-127 PR4 CI catch: two join specs expected exactly that).
export const CREATOR_KEY_PREFIX = "tq_created_";

// Profile ids this device has already been shown, per household. The device setup flow
// answers "who shows here" once, at join — but a child added months later was appearing on
// every configured device silently, because nothing re-asked. This is the list that makes
// a new arrival noticeable: any profile not in it gets one prompt, then joins the list
// either way. Seeded (without prompting) the first time a device reads it, so existing
// households don't get a burst of prompts for children they already know about.
export const SEEN_CHILDREN_KEY_PREFIX = "tq_seen_children_";

// This device's own identity — a random UUID, generated once and reused for the life of
// this browser install (see src/lib/api.js's getDeviceId()). Independent of which
// household this device is currently in: it's what lets Parent Mode → Settings → Devices
// list this browser/install as a row, and what lets "Remove" kick it out. Not a secret —
// it identifies the device, not a person, and proves nothing on its own.
export const DEVICE_ID_KEY = "tq_device_id";
// Turnstile SITE key (W3.5 waitlist) — public by design (it renders in every visitor's
// page source); the pairing SECRET lives only as an encrypted Worker secret
// (TURNSTILE_SECRET). One widget covers littlebankers.uk + staging.littlebankers.uk.
export const TURNSTILE_SITE_KEY = "0x4AAAAAAEcfWaJ2NpyDEvZA";

export const uid = () => Math.random().toString(36).slice(2, 10);

export const nowIso = () => new Date().toISOString();

export const CODE_CHARS = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

// LB-187: both generators below are credentials, so they draw from crypto.getRandomValues
// with rejection sampling (uniform over the alphabet) rather than Math.random.
const randomFromAlphabet = (alphabet, length) => {
  let out = "";
  const bytes = new Uint8Array(length * 2);
  const limit = 256 - (256 % alphabet.length);
  while (out.length < length) {
    crypto.getRandomValues(bytes);
    for (const b of bytes) {
      if (out.length >= length) break;
      if (b < limit) out += alphabet[b % alphabet.length];
    }
  }
  return out;
};

export const genCode = () => randomFromAlphabet(CODE_CHARS, 6);

// Same unambiguous-character philosophy as CODE_CHARS (no 0/O, 1/l/I) but a larger
// alphabet including mixed case, since a passcode gets typed less often than the
// household code and benefits from the extra entropy.
export const PASSCODE_CHARS = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";

export const genPasscode = (length = 10) => randomFromAlphabet(PASSCODE_CHARS, length);

// Printable ASCII only (0x21–0x7E) — alphanumeric plus symbols, no spaces, no control or
// escape characters. Enforced again server-side (worker/lib/passcode.js) as the real rule;
// this copy just drives the live rules checklist/validation in Settings.
export const PASSCODE_RE = /^[!-~]{8,30}$/;

export const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

export const AVATARS = ["🤖", "🚀", "🐱", "🐶", "👻", "⭐", "⚡", "👑", "👸", "🦄", "🐰", "🦋", "🌸", "🐼", "🦁", "🐨"];
// Rainbow palette kids can pick for their Balance Hero Card — applied via inline gradient styles
// (rather than dynamic Tailwind class names) so the color always renders correctly.

export const CARD_COLORS = {
  red: { label: "Red", swatch: "#ef4444", from: "#f87171", to: "#b91c1c" },
  orange: { label: "Orange", swatch: "#f97316", from: "#fb923c", to: "#c2410c" },
  yellow: { label: "Yellow", swatch: "#eab308", from: "#facc15", to: "#a16207" },
  green: { label: "Green", swatch: "#22c55e", from: "#4ade80", to: "#15803d" },
  blue: { label: "Blue", swatch: "#3b82f6", from: "#60a5fa", to: "#1d4ed8" },
  indigo: { label: "Indigo", swatch: "#6366f1", from: "#818cf8", to: "#4338ca" },
  violet: { label: "Violet", swatch: "#a855f7", from: "#c084fc", to: "#7e22ce" },
};

// Single source of truth for the currency symbol shown throughout the app (exchange rate,
// bank balances, spending caps, goal costs, etc.) — previously a literal "£" pasted into
// ~20 different places. This only centralizes the SYMBOL; the underlying data model still
// assumes GBP specifically (vaultBalancePence stores pence, spendingCap.weeklyGBP is named
// for GBP) — swapping this constant alone does not make the app multi-currency. Doing that
// properly would mean a per-household currency setting, a data migration for the pence-
// specific field names, and updating the exchange-rate mental model in the wizard/Settings
// copy — worth doing if/when a non-GBP household actually needs it, not speculatively now.
export const CURRENCY_SYMBOL = "£";

// Single localisation point for NUMBER FORMATTING (thousands separators, decimal marks,
// currency layout) — everything in src/lib/utils.js's fmtNumber/fmtGBP/fmtPence formats via
// Intl.NumberFormat driven by these two constants, so switching a future household to
// another locale/currency display is a one-line change here (plus the data-model caveats on
// CURRENCY_SYMBOL above — this localises FORMATTING only, the underlying model is still
// GBP/pence). "en-GB" gives 1,234 and £1,234.56 today, i.e. byte-identical output to the
// old toFixed(2) rendering for every value below 1,000 — Playwright's exact-string
// assertions on small balances are unaffected.
export const APP_LOCALE = "en-GB";
export const APP_CURRENCY = "GBP";

export const VOCAB_TEST_SIZE = 15;

// Words per test under Learning Coach. Alan, 14 Sep: a default with a free-format parent
// override, since pace through a band is largely a function of how many words a test asks.
// Stored per child, and NOT tied to the manual/Coach switch — changing it is a Coach setting,
// not a departure from Coach.
export const DEFAULT_COACH_TEST_SIZE = 15;

// The pass mark for every game that scores by percentage: spelling, vocabulary and the Maths
// Workout. Alan, 13 Sep, dropped it from 80 to 75, alongside spelling's retry round becoming
// practice that no longer counts — the two move in opposite directions and roughly cancel for
// a child who misses two or three words.
//
// Times Tables is deliberately NOT governed by this: it passes on a streak COUNT
// (lc.ttStreakPassCount), not a percentage, so there is no equivalent number to move.
//
// Vocabulary and maths store their own value per child and a parent can change it; this is
// the default for a new child, and what migrateState shifts an untouched 80 to, once.
export const DEFAULT_PASS_THRESHOLD = 75;

// Default weekly spending cap for a child, in £ — on by default (see migrateState /
// ProfilesTab's addChild), editable per child from the Rewards tab.
export const DEFAULT_SPENDING_CAP_GBP = 5;

// Spending guardrails (SPENDING_GUARDRAILS_SCOPE.md, Aug 2026). These live here rather than
// in spendingDefaults.js because BOTH that engine and utils.js's migration/seeding need
// them, and constants.js is the only module either can import without creating a cycle.
//
// The busy-week split: English payout / Maths payout / habits streak / one earning task.
// The Learn & Earn skew (60% of the cap across the two subjects) is deliberate — it is the
// app's differentiator, so it is where a child's earning should mostly come from. The task
// share is the remainder, so the four slots always sum to exactly the cap however the other
// three round.
export const EARN_SPLIT = { english: 0.30, maths: 0.30, habit: 0.20 };

// Below this a reward stops reading as a reward: a 1-token task is not worth a child's
// attention. "Too low to bother with" fails quietly, unlike an over-cap value, which is
// precisely why it needs a floor rather than trusting it to be noticed.
export const MIN_REWARD_TOKENS = 2;

// STANDARD SETS — what a child is set up with, before anything is priced.
//
// These are the baked FALLBACK. The live sets are CMS entries in the `starter-set` area
// (LB-131), so the wording can be tuned after watching real households without a deploy.
// Names and age band only: prices are never authored here or in the CMS, because every
// amount is derived from that child's own cap at seed time (LB-132). A price in a content
// entry would sit outside the guardrails engine and break the rule that everything derives
// from the cap.
//
// Split at AGE_UP_THRESHOLD (LB-130), reusing the app's one age binary rather than
// introducing a second. Chores do not ladder the way maths does — "put your toys away"
// versus "load the dishwasher" is one step, not eight — so a binary is the right
// instrument and the school year stays where it belongs.
//
// `weight` distributes that child's TASK slot across the tasks in the set, and the weights
// in each set sum to 1. Heavier weight = the task worth more, not the task done more often.
export const STARTER_TASKS_YOUNG = [
  { label: "Put your toys away", weight: 0.2 },
  { label: "Help lay the table", weight: 0.2 },
  { label: "Read a short book", weight: 0.25 },
  { label: "Tidy your room", weight: 0.35 },
];
export const STARTER_TASKS_OLDER = [
  { label: "Read a short book", weight: 0.15 },
  { label: "Help with dinner", weight: 0.2 },
  { label: "Tidy your room", weight: 0.25 },
  { label: "Read a long book", weight: 0.4 },
];

// Daily habits are the checked-off-every-day set behind the streak. Nothing was ever seeded
// here (LB-132) — habitReward and habitThreshold were set and then no habits existed to
// earn them, so a fifth of every child's cap was simply unreachable and the Daily habits
// section opened empty on a brand-new household.
//
// No weights: the streak reward is ONE amount for completing the whole set, so individual
// habits carry no price. That is also why a set can be added to freely — a longer list
// makes the streak harder, never more expensive.
export const STARTER_HABITS_YOUNG = ["Brush your teeth", "Get dressed by yourself", "Put your shoes away"];
export const STARTER_HABITS_OLDER = ["Brush your teeth", "Make your bed", "Pack your school bag"];

// Kept as an alias so nothing that imported the old name breaks at the seam; the older set
// is the closer match to what it used to hold.
export const STARTER_TASKS = STARTER_TASKS_OLDER;
// Shared by the sticky tab menu (rendered in Root, frozen alongside the child chips) and
// ParentView (which uses the ids to decide what content to show) — one source of truth for
// which parent tabs exist and their order. Settings isn't here — it's reached via the header
// gear icon instead, see App's header.

// Icon + colour per activity "kind", shared across every ActivityRow caller so an earn always
// looks the same whether it's showing on Home, Rewards, or Recently Resolved. Transaction rows
// (credit/spend/debit) and request rows (goal_redeem/habit_reward/vault_transfer/experience_redeem)
// are different vocabularies living in the same lookup since callers only ever need one or the
// other, never both at once.
// Sep 2026 (docs/DESIGN_GUIDANCE.md §6): `icon` is a sprite id from little-bankers-icons.svg
// ("coin" = the shared CoinIcon, "vault" = the shared VaultIcon), rendered by ActivityRow in
// Common.jsx. Emoji are retired from the feed in both modes — this table is a shared
// primitive, not a kid component, so it changes here rather than in the kid icon PR.
export const ACTIVITY_KIND_STYLE = {
  credit: { icon: "coin", iconBgClass: "bg-emerald-50", iconColorClass: "text-emerald-600", amountColorClass: "text-emerald-500" },
  spend: { icon: "ic-till", iconBgClass: "bg-amber-50", iconColorClass: "text-amber-600", amountColorClass: "text-amber-600" },
  debit: { icon: "ic-fix", iconBgClass: "bg-rose-50", iconColorClass: "text-rose-500", amountColorClass: "text-rose-500" },
  goal_redeem: { icon: "ic-goals", iconBgClass: "bg-violet-50", iconColorClass: "text-violet-600" },
  habit_reward: { icon: "ic-flame", iconBgClass: "bg-amber-50", iconColorClass: "text-amber-600" },
  vault_transfer: { icon: "vault", iconBgClass: "bg-teal-50", iconColorClass: "text-teal-600" },
  experience_redeem: { icon: "ic-gift", iconBgClass: "bg-pink-50", iconColorClass: "text-pink-600" },
  // LB-17: a child's ask to buy something — the wish glyph, spend gold.
  ask_to_buy: { icon: "ic-wish", iconBgClass: "bg-amber-50", iconColorClass: "text-amber-600" },
  earn: { icon: "coin", iconBgClass: "bg-emerald-50", iconColorClass: "text-emerald-600" },
  learn_earn_reward: { icon: "ic-brain", iconBgClass: "bg-purple-50", iconColorClass: "text-purple-600" },
  // LB-356: an Oinks movement (parent Give Oinks, sibling gift). Oinks are never money, so the
  // row takes the Piggington pink and the Oinks glyph, never the coin or the token greens.
  oinks: { icon: "oink", iconBgClass: "bg-pink-50", iconColorClass: "text-pink-600", amountColorClass: "text-pink-600" },
  // W4 rate marker (zero-amount feed row, see utils.rateMarkerTxn) — deliberately muted.
  rate_marker: { icon: "ic-exchange", iconBgClass: "bg-slate-100", iconColorClass: "text-slate-500", amountColorClass: "text-slate-400" },
};

// Cap chosen for BALANCE MAGNITUDE, not for display precision (Alan, Aug 2026). Because
// rescaleHouseholdRewards moves every reward value with the rate during setup, the rate never
// changes what anything is worth in £ — only how many digits everyone looks at. At rate 100,
// £5/week of pocket money is 500 tokens and a year unspent is 26,000; at 20 it's 5,200, which
// stays inside the 4 digits the hero card's columns handle without stacking. 20 still permits
// 5p granularity, finer than most households will use.
export const MAX_EXCHANGE_RATE = 20;

// Pence value of one token, plus whether that value is exact. Only rates dividing 100 exactly
// give a clean per-token figure: at rate 3 the true value is 33.3p, and 3 x 33p is 99p, not
// £1 — so the UI says "≈" rather than asserting arithmetic that doesn't hold. This matters
// inside the cap too, not just for legacy rates: only 6 of the 20 permitted rates are exact,
// and several collide once rounded (16, 17 and 18 all show 6p).
export function tokenValueParts(rate) {
  const r = rate > 0 ? rate : 10;
  return { pence: Math.round(100 / r), exact: 100 % r === 0 };
}

// LB-337 — the owner's first name. 40 characters, trimmed, and nothing else: the ticket is
// explicit that there is no other validation. A name is not a credential (see the save
// handler in worker/index.js for why it is deliberately NOT a server-owned field), so the
// only job here is to keep a household document from carrying an essay, and to keep the
// same ceiling on both the client and the worker. Collapsing internal whitespace as well
// as trimming the ends: "Alan   " and "Al  an" both come from a real keyboard, and the
// second one reads as a typo everywhere the name is greeted.
export const OWNER_NAME_MAX = 40;

export function cleanOwnerName(value) {
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/\s+/g, " ").trim().slice(0, OWNER_NAME_MAX);
  return cleaned || null;
}
